import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { FileText, Receipt, Paperclip, History } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const RES = { ACC: ["ACC", "bg-emerald-100 text-emerald-800"], REVISI: ["Revisi", "bg-amber-100 text-amber-800"] };
const ST_ID = { WORKING: "Dikerjakan", WAITING: "Menunggu customer", REVISION: "Revisi", APPROVED: "ACC" };
const fmt = (s) => (s ? new Date(s).toLocaleString("id-ID", { dateStyle: "short", timeStyle: "short" }) : "");
const label = (r) => (r.kind === "REVISI" ? `Revisi ${r.no}` : r.kind === "ADJUSTMENT" ? `Adjustment ${r.no}` : "Design");

export const WoRefs = ({ h }) => (
  <div className="flex flex-wrap gap-4 text-sm mb-4" data-testid="wo-refs">
    <div><div className="text-xs text-slate-500">Quotation</div>
      {h?.quotation ? <Link to={`/app/quotations?open=${h.quotation.id}`} className="inline-flex items-center gap-1 font-mono hover:underline" data-testid="wo-ref-quotation"><FileText className="w-3.5 h-3.5" />{h.quotation.code}</Link> : <span className="text-slate-400" data-testid="wo-ref-quotation">-</span>}
    </div>
    <div><div className="text-xs text-slate-500">Invoice</div>
      {h?.invoices?.length ? h.invoices.map((i) => <Link key={i.id} to={`/app/invoices?open=${i.id}`} className="inline-flex items-center gap-1 font-mono mr-2 hover:underline" data-testid={`wo-ref-invoice-${i.code}`}><Receipt className="w-3.5 h-3.5" />{i.code}</Link>) : <span className="text-slate-400" data-testid="wo-ref-invoice">Belum ada</span>}
    </div>
  </div>
);

const Files = ({ files }) => (files?.length ? (
  <div className="flex flex-wrap gap-2 mt-1">
    {files.map((f) => <a key={f.path} href={`${BACKEND}/api/files/${f.path}`} target="_blank" rel="noreferrer" className="text-xs inline-flex items-center gap-1 text-sky-700 hover:underline"><Paperclip className="w-3 h-3" />{f.filename}</a>)}
  </div>
) : null);

export const RevisionHistory = ({ woId }) => {
  const [d, setD] = useState(null);
  useEffect(() => { if (woId) { api.get(`/prod/wo/${woId}/revisions`).then(({ data }) => setD(data)).catch(() => setD(null)); } }, [woId]);
  if (!d || !d.revisions.length) return null;
  return (
    <div className="mb-5" data-testid="wo-revisions">
      <div className="text-xs font-mono text-slate-500 mb-2">RIWAYAT DATA PRODUKSI · {d.revision_count}× revisi · {d.edit_count} file diedit</div>
      <div className="space-y-2">
        {d.revisions.map((r) => (
          <div key={r.revision} className={`rounded-lg border p-2 text-sm ${r.revision ? "border-violet-200 bg-violet-50/50" : "border-slate-200"}`} data-testid={`wo-rev-${r.revision}`}>
            <div className="flex flex-wrap gap-2 items-center">
              <b>{r.revision ? `${r.kind === "SUSULAN" ? "File Tertinggal" : "Revisi Data"} ${r.revision}` : "Data Awal"}</b>
              <span className="text-xs text-slate-500">{fmt(r.at)}{r.by ? ` · ${r.by}` : ""}</span>
              <span className={`ml-auto text-xs ${r.released_at ? "text-emerald-700" : "text-amber-700"}`}>{r.released_at ? `ke Operator ${fmt(r.released_at)}${r.released_by ? ` · ${r.released_by}` : ""}` : "belum ke Operator"}</span>
            </div>
            {r.note && <div className="text-xs italic text-slate-600">“{r.note}”</div>}
            <div className="text-xs text-slate-600 mt-1">{r.files.map((f) => `${f.file_name}${f.updated_at ? ` (diedit ${fmt(f.updated_at)})` : ""}`).join(" · ")}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const ApprovalHistory = ({ h }) => {
  const rounds = h?.rounds || [];
  if (!rounds.length && !h?.approvals?.length) return null;
  const apvFor = (r) => (h.approvals || []).filter((a) => a.round_label === label(r));
  return (
    <div className="border border-slate-200 rounded-xl p-3 mb-4" data-testid="approval-history">
      <div className="text-xs font-mono text-slate-500 mb-2 flex items-center gap-1"><History className="w-3.5 h-3.5" /> RIWAYAT APPROVAL</div>
      <ol className="space-y-3">
        {rounds.map((r, i) => (
          <li key={i} className="text-sm border-l-2 border-slate-200 pl-3" data-testid={`history-round-${i}`}>
            <div className="font-semibold">{label(r)} <span className="text-xs font-normal text-slate-500">· {ST_ID[r.status] || r.status}{r.at ? ` · ${fmt(r.at)}` : ""}{r.by ? ` · ${r.by}` : ""}</span></div>
            {r.note && r.kind !== "REVISI" && <div className="text-xs text-slate-500">{r.note}</div>}
            <Files files={r.files} />
            {apvFor(r).map((a) => (
              <div key={a.id} className="mt-1 text-xs text-slate-600" data-testid={`history-apv-${a.id}`}>
                Dikirim: {a.sent_wa_at ? `WA ${fmt(a.sent_wa_at)} ` : ""}{a.sent_email_at ? `Email ${fmt(a.sent_email_at)} ` : ""}{!a.sent_wa_at && !a.sent_email_at ? "belum" : ""}{a.sent_by ? `oleh ${a.sent_by}` : ""}
                {a.result && <div><span className={`px-1.5 py-0.5 rounded ${RES[a.result]?.[1]}`}>{RES[a.result]?.[0]}</span> {a.responded_via === "link" ? "oleh customer lewat link" : `dicatat ${a.responded_by || ""}`} · {fmt(a.responded_at)}</div>}
                {(a.customer_note || a.response_note) && <div className="italic">"{a.customer_note || a.response_note}"</div>}
              </div>
            ))}
          </li>
        ))}
      </ol>
    </div>
  );
};

export const useWoHistory = (woId) => {
  const [h, setH] = useState(null);
  useEffect(() => { setH(null); if (woId) { api.get(`/design/wo/${woId}/history`).then(({ data }) => setH(data)).catch(() => {}); } }, [woId]);
  return h;
};
